import { useEffect, useRef, useState } from 'react';
import { Autocomplete, type AutocompleteOption } from '@nexvia-logistics/ui';
import { MapPin } from '@nexvia-logistics/ui/icons';
import {
  MIN_ADDRESS_QUERY_LENGTH,
  type AddressSearch,
  type AddressSuggestion,
  type ResolvedAddress
} from '@/shared/google-places';

/** Same pause as nexvia-web-app's `LocationAutocomplete` (`debounce: 300`) before asking Google. */
const SUGGEST_DEBOUNCE_MS = 300;

interface AddressAutocompleteFieldProps {
  search: AddressSearch;
  label: string;
  /** `FormField`'s ids, so its label and hint reach this field's input. */
  id: string;
  'aria-describedby'?: string;
  disabled?: boolean;
  placeholder?: string;
  value: string;
  /** Free text — typed, cleared, or a suggestion still being resolved: the form drops the coordinates. */
  onType: (value: string) => void;
  /** A picked suggestion, resolved: the address to store and where it is. */
  onResolve: (resolved: ResolvedAddress) => void;
}

/**
 * NEX-1168 (plan 2026-10-20 Tarea 2.7) — `LocationForm`'s address with Google's suggestions: the
 * design system's `Autocomplete` (the provider is injected: it never calls Google itself) over the
 * `AddressSearch` the page hands down. Typing stays free — an address Google does not know is
 * typed and saved by hand, without coordinates.
 *
 * Every keystroke and every pick takes a ticket, and an answer for an older one is dropped: a slow
 * suggestion list never lands over a newer query or over the address just picked.
 */
export function AddressAutocompleteField({
  search,
  label,
  id,
  'aria-describedby': describedBy,
  disabled,
  placeholder,
  value,
  onType,
  onResolve
}: AddressAutocompleteFieldProps) {
  const [results, setResults] = useState<AddressSuggestion[]>([]);
  const [loading, setLoading] = useState(false);
  const pendingRef = useRef<{ ticket: number; timer: ReturnType<typeof setTimeout> | undefined }>({
    ticket: 0,
    timer: undefined
  });

  useEffect(() => {
    const pending = pendingRef.current;
    return () => {
      pending.ticket += 1;
      clearTimeout(pending.timer);
    };
  }, []);

  /** Cancels whatever is in flight and returns the new ticket. */
  const nextTicket = () => {
    const pending = pendingRef.current;
    clearTimeout(pending.timer);
    pending.ticket += 1;
    return pending.ticket;
  };

  const handleInput = (next: string) => {
    onType(next);
    const ticket = nextTicket();
    if (next.trim().length < MIN_ADDRESS_QUERY_LENGTH) {
      setResults([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    pendingRef.current.timer = setTimeout(() => {
      void search
        .suggest(next)
        .catch((): AddressSuggestion[] => [])
        .then((found) => {
          if (ticket !== pendingRef.current.ticket) return;
          setResults(found);
          setLoading(false);
        });
    }, SUGGEST_DEBOUNCE_MS);
  };

  const handleSelect = (option: AutocompleteOption) => {
    const ticket = nextTicket();
    const picked = results.find((result) => result.id === option.id);
    // Shown right away, and kept as typed text if Google cannot resolve it.
    onType(picked?.secondary ? `${picked.label}, ${picked.secondary}` : option.label);
    setResults([]);
    setLoading(true);
    void search
      .resolve(option.id)
      .catch((): ResolvedAddress | null => null)
      .then((resolved) => {
        if (ticket !== pendingRef.current.ticket) return;
        setLoading(false);
        if (resolved) onResolve(resolved);
      });
  };

  const handleClear = () => {
    nextTicket();
    setResults([]);
    setLoading(false);
    onType('');
  };

  return (
    <Autocomplete
      id={id}
      aria-describedby={describedBy}
      label={label}
      disabled={disabled}
      placeholder={placeholder}
      value={value}
      icon={<MapPin size={18} />}
      loading={loading}
      results={results}
      onInputChange={handleInput}
      onSelect={handleSelect}
      onClear={handleClear}
    />
  );
}
